/**
 * Where you are in the list, and the way to the next page.
 *
 * Previous and Next, not page numbers: the list is read a page at a time
 * from wherever the last page ended, which is exact and costs a page however
 * long the list is. A numbered jump to "page 37" would have to read the
 * thirty-six pages before it to know where it starts.
 */
import { useId } from 'react';
import { PAGE_SIZES, type PageSize } from './libraryQuery';
import type { LibraryData } from './useLibrary';
import styles from './Library.module.css';

const n = (v: number) => v.toLocaleString('en-US');

export function rangeText(data: Pick<LibraryData, 'from' | 'to' | 'total' | 'mode' | 'tooMany'>): string {
  if (data.to === 0) return '';
  const range = `${n(data.from)}–${n(data.to)}`;
  if (data.total === null) return range;
  return `${range} of ${n(data.total)}`;
}

export function LibraryPager({ data, size, onSize, onPrevious, onNext }: {
  data: LibraryData;
  size: PageSize;
  onSize: (size: PageSize) => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const id = useId();
  if (data.mode === 'named') return null;
  return (
    <nav className={styles.pager} aria-label="Pages of policies">
      <p className={styles.range} role="status" style={{ margin: 0 }}>
        {data.isUpdating ? (
          // The rows on screen are still the last answer. Saying "51–100" over rows 1–50 would be false for as long as it lasted.
          <span>Loading…</span>
        ) : (
          <>
            <b className="tnum">{rangeText(data)}</b>
            {data.total !== null && data.to > 0 && (data.total === 1 ? ' policy' : ' policies')}
          </>
        )}
        {data.tooMany && !data.isUpdating && ' match. The first 1,000 can be paged through; narrow the search to reach the rest.'}
      </p>
      <div className={styles.pagerControls}>
        <span className={styles.pageSize}>
          <label htmlFor={id}>Rows per page</label>
          <select id={id} className={styles.filterSelect} value={size} onChange={(e) => onSize(Number(e.target.value) as PageSize)}>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </span>
        <button type="button" className={styles.pageBtn} disabled={!data.hasPrevious || data.isUpdating} onClick={onPrevious}>Previous</button>
        <button type="button" className={styles.pageBtn} disabled={!data.hasNext || data.isUpdating} onClick={onNext}>Next</button>
      </div>
    </nav>
  );
}
